// src/pages/Orders/Orders.jsx
import React, { useState, useEffect } from "react";
import { getOrders } from "../pages/api";
import { useCurrency } from "../Context/CurrencyContext"; // ADD THIS
import "./Orders.css";

const Orders = () => {
  const { formatPrice } = useCurrency(); // FIXED: Use currency context
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0
  });

  // ---------- Modal state ----------
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // ---------- Image Zoom Popup state ----------
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImagePopup, setShowImagePopup] = useState(false);

  // ---------- Helper functions ----------
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // ---------- Extract order items from various possible structures ----------
  const getOrderItems = (order) => {
    if (!order) return [];
    
    if (order.items && Array.isArray(order.items)) return order.items;
    if (order.orderItems && Array.isArray(order.orderItems)) return order.orderItems;
    if (order.products && Array.isArray(order.products)) return order.products;
    if (order.line_items && Array.isArray(order.line_items)) return order.line_items;

    if (order.items && typeof order.items === 'string') {
      try {
        const parsed = JSON.parse(order.items);
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {
        console.warn('Failed to parse items JSON', e);
      }
    }

    if (order.orderItems && typeof order.orderItems === 'string') {
      try {
        const parsed = JSON.parse(order.orderItems);
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {
        console.warn('Failed to parse orderItems JSON', e);
      }
    }

    return [];
  };

  // ---------- Get total item count (sum of quantities) ----------
  const getTotalItemCount = (order) => {
    const items = getOrderItems(order);
    if (items.length === 0) return 0;
    return items.reduce((total, item) => total + (item.quantity || item.qty || 1), 0);
  };

  // ---------- Parse selected attributes (JSON string -> array) ----------
  const parseSelectedAttributes = (item) => {
    if (!item.selectedAttributes) return [];
    if (Array.isArray(item.selectedAttributes)) return item.selectedAttributes;
    if (typeof item.selectedAttributes === 'string') {
      try {
        const parsed = JSON.parse(item.selectedAttributes);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    }
    return [];
  };

  // ---------- Format attributes for display – shows "Size: 12" ----------
  const formatAttributes = (item) => {
    const attrs = parseSelectedAttributes(item);
    if (attrs.length === 0) return null;
    return `Size: ${attrs.join(', ')}`;
  };

  // ---------- Construct full image URL ----------
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    const baseURL = 'https://volumaire.backendapihub.com';
    return `${baseURL}${imagePath}`;
  };

  // ---------- Order data extractors ----------
  const getOrderId = (order) => {
    return order.orderNumber || order.orderId || order.id || order._id || 'N/A';
  };

  const getOrderStatus = (order) => {
    return order.status || order.orderStatus || 'pending';
  };

  const getPaymentStatus = (order) => {
    return order.paymentStatus || order.payment_status || 'N/A';
  };

  const getPaymentMethod = (order) => {
    return order.paymentMethod || order.payment_method || 'N/A';
  };

  // ---------- Status link color classes ----------
  const getStatusLinkClass = (status) => {
    if (!status) return 'status-pending-link';
    const statusLower = status.toLowerCase();
    if (statusLower.includes('delivered') || statusLower.includes('completed')) {
      return 'status-delivered-link';
    } else if (statusLower.includes('processing') || statusLower.includes('confirmed')) {
      return 'status-processing-link';
    } else if (statusLower.includes('shipped') || statusLower.includes('dispatched')) {
      return 'status-shipped-link';
    } else if (statusLower.includes('cancelled') || statusLower.includes('canceled')) {
      return 'status-cancelled-link';
    } else if (statusLower.includes('returned') || statusLower.includes('refunded')) {
      return 'status-returned-link';
    } else {
      return 'status-pending-link';
    }
  };

  const formatStatus = (status) => {
    if (!status) return 'Pending';
    const statusLower = status.toLowerCase();
    if (statusLower.includes('confirmed')) return 'Processing';
    if (statusLower.includes('dispatched')) return 'Shipped';
    if (statusLower.includes('completed')) return 'Delivered';
    if (statusLower.includes('canceled')) return 'Cancelled';
    if (statusLower.includes('returned')) return 'Returned';
    if (statusLower.includes('refunded')) return 'Refunded';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const formatPaymentStatus = (status) => {
    if (!status || status === 'N/A') return 'N/A';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getOrderTotal = (order) => {
    return order.totalAmount || order.totalPrice || order.amount || order.finalAmount || 0;
  };

  const getOrderDate = (order) => {
    return order.createdAt || order.orderDate || order.date || order.createdDate;
  };

  // ---------- Modal handlers ----------
  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedOrder(null);
  };

  // ---------- Image Zoom Popup handlers ----------
  const handleImageClick = (imageUrl, productName) => {
    setSelectedImage({ url: imageUrl, name: productName });
    setShowImagePopup(true);
  };

  const closeImagePopup = () => {
    setShowImagePopup(false);
    setSelectedImage(null);
  };

  // ---------- Fetch orders ----------
  const fetchOrders = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";
      
      if (!token || !isLoggedIn) {
        setError("Please login to view your orders.");
        setOrders([]);
        setLoading(false);
        return;
      }

      const response = await getOrders({ 
        page, 
        limit: 10,
        _t: new Date().getTime()
      });
      
      let ordersData = [];
      let paginationData = {};
      
      console.log("Orders API Response:", response);
      
      if (response) {
        if (response.data && Array.isArray(response.data)) {
          ordersData = response.data;
          paginationData = response.pagination || {};
        }
        else if (response.orders && Array.isArray(response.orders)) {
          ordersData = response.orders;
          paginationData = response.pagination || {};
        }
        else if (Array.isArray(response)) {
          ordersData = response;
        }
        else if (response.data && response.data.orders && Array.isArray(response.data.orders)) {
          ordersData = response.data.orders;
          paginationData = response.data.pagination || {};
        }
        else if (response.items && Array.isArray(response.items)) {
          ordersData = response.items;
          paginationData = response.pagination || response.meta || {};
        }
        else if (response.results && Array.isArray(response.results)) {
          ordersData = response.results;
          paginationData = response.pagination || response.meta || {};
        }
        else if (typeof response === 'object') {
          const keys = Object.keys(response);
          for (let key of keys) {
            if (Array.isArray(response[key]) && (key.includes('order') || key.includes('item'))) {
              ordersData = response[key];
              break;
            }
          }
        }
      }
      
      if (ordersData.length === 0 && response && typeof response === 'object') {
        setError("No orders found.");
      }
      
      setOrders(ordersData);
      
      setPagination({
        page: paginationData.page || page,
        totalPages: paginationData.totalPages || paginationData.pages || 1,
        total: paginationData.total || paginationData.count || ordersData.length
      });
      
    } catch (err) {
      console.error("Error fetching orders:", err);
      
      if (err.requiresLogin || err.message?.includes('login') || err.message?.includes('unauthorized') || err.message?.includes('expired')) {
        setError("Please login to view your orders.");
        localStorage.removeItem("token");
        localStorage.removeItem("isLoggedIn");
      } 
      else if (err.response) {
        const status = err.response.status;
        const errorMessage = err.response.data?.message || err.response.data?.error;
        
        switch (status) {
          case 401:
            setError("Session expired. Please login again.");
            localStorage.removeItem("token");
            localStorage.removeItem("isLoggedIn");
            break;
          case 403:
            setError("You don't have permission to view orders");
            break;
          case 404:
            setError("No orders found");
            break;
          case 500:
            setError("Server error. Please try again later");
            break;
          default:
            setError(errorMessage || `Error ${status}: Failed to fetch orders`);
        }
      } else if (err.request) {
        setError("No response from server. Please check your internet connection");
      } else if (err.message && err.message.includes('timeout')) {
        setError("Request timeout. Please try again");
      } else if (err.message && err.message.includes('Network Error')) {
        setError("Network error. Please check your internet connection");
      } else {
        setError(err.message || "Failed to load orders");
      }
      
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchOrders(newPage);
    }
  };

  const handleRetry = () => {
    fetchOrders(pagination.page);
  };

  const handleLogin = () => {
    window.location.href = `/login?redirect=${encodeURIComponent('/orders')}`;
  };

  const loginPromptContainer = () => (
    <div className="login-prompt-container">
      <div className="login-prompt-icon">🔒</div>
      <h3 className="login-prompt-title">Login Required</h3>
      <p className="login-prompt-message">
        Please login to view your order history and track your orders.
      </p>
      <button onClick={handleLogin} className="login-prompt-button">
        Login Now
      </button>
      <p className="login-prompt-note">
        Don't have an account? <a href="/register">Sign up here</a>
      </p>
    </div>
  );

  return (
    <div className="orders-page">
      <h2 className="page-heading">My Orders</h2>
      
      <div className="orders-container">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading your orders...</p>
          </div>
        ) : error && (error.includes("login") || error.includes("Login") || error.includes("Please login")) ? (
          loginPromptContainer()
        ) : error ? (
          <div className="error-container">
            <div className="error-icon">!</div>
            <p className="error-message">{error}</p>
            <button onClick={handleRetry} className="retry-button">
              Try Again
            </button>
          </div>
        ) : orders.length === 0 ? (
          <div className="empty-orders-container">
            <div className="empty-icon">📦</div>
            <h3 className="empty-title">No Orders Found</h3>
            <p className="empty-message">
              You haven't placed any orders yet.
            </p>
            <button 
              className="shop-now-btn"
              onClick={() => window.location.href = '/products'}
            >
              Start Shopping
            </button>
          </div>
        ) : (
          <>
            <div className="orders-stats">
              <div className="stat-card">
                <span className="stat-label">Total Orders</span>
                <span className="stat-value">{pagination.total}</span>
              </div>
              <div className="stat-card">
                <span className="stat-label">Current Page</span>
                <span className="stat-value">{pagination.page} of {pagination.totalPages}</span>
              </div>
            </div>

            <div className="orders-table-responsive">
              <table className="orders-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Items</th>
                    <th>Total Amount</th>
                    <th>Order Status</th>
                    <th>Payment Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order, index) => (
                    <tr key={order.id || order._id || order.orderId || index}>
                      <td className="order-date">
                        {formatDate(getOrderDate(order))}
                      </td>
                      <td className="order-items-count">
                        {getTotalItemCount(order)}
                      </td>
                      <td className="order-total">
                        <strong>{formatPrice(getOrderTotal(order))}</strong> {/* FIXED: Use formatPrice */}
                      </td>
                      <td className="order-status">
                        <a 
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handleViewOrder(order);
                          }}
                          className={`status-link ${getStatusLinkClass(getOrderStatus(order))}`}
                        >
                          {formatStatus(getOrderStatus(order))}
                        </a>
                      </td>
                      <td className="payment-status">
                        <span className={`payment-status-text payment-${getPaymentStatus(order).toLowerCase()}`}>
                          {formatPaymentStatus(getPaymentStatus(order))}
                        </span>
                      </td>
                      <td className="order-actions">
                        <button
                          onClick={() => handleViewOrder(order)}
                          className="action-btn view-btn"
                          title="View Order Details"
                          disabled={!order.id && !order._id && !order.orderId}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="pagination-container">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="pagination-btn prev-btn"
                >
                  Previous
                </button>
                
                <div className="page-numbers">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`page-number ${pagination.page === pageNum ? 'active' : ''}`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                  className="pagination-btn next-btn"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ---------- Order Details Modal (with clickable images) ---------- */}
      {showModal && selectedOrder && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Order Details</h3>
              <button className="modal-close-btn" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="order-info">
                <div className="info-row">
                  <span className="info-label">Date:</span>
                  <span className="info-value">{formatDate(getOrderDate(selectedOrder))}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Order Status:</span>
                  <span className={`status-link ${getStatusLinkClass(getOrderStatus(selectedOrder))}`}>
                    {formatStatus(getOrderStatus(selectedOrder))}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Payment Status:</span>
                  <span className="payment-status-modal">
                    {formatPaymentStatus(getPaymentStatus(selectedOrder))}
                    {getPaymentMethod(selectedOrder) !== 'N/A' && ` via ${getPaymentMethod(selectedOrder)}`}
                  </span>
                </div>
              </div>

              <div className="items-table-responsive">
                <table className="items-table">
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th>Product</th>
                      <th>Price</th>
                      <th>Qty</th>
                      <th>Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getOrderItems(selectedOrder).map((item, idx) => {
                      const name = item.name || item.productName || item.title || `Item ${idx + 1}`;
                      const price = parseFloat(item.price) || 0;
                      const quantity = item.quantity || item.qty || 1;
                      const subtotal = price * quantity;
                      const imageUrl = getImageUrl(item.image);
                      const sizeText = formatAttributes(item);
                      
                      return (
                        <tr key={idx}>
                          <td className="product-image-cell">
                            {imageUrl ? (
                              <img 
                                src={imageUrl} 
                                alt={name} 
                                className="product-thumbnail clickable-image"
                                onClick={() => handleImageClick(imageUrl, name)}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            ) : (
                              <span className="no-image">—</span>
                            )}
                          </td>
                          <td className="product-details-cell">
                            <div className="product-name">{name}</div>
                            {sizeText && (
                              <div className="product-size">{sizeText}</div>
                            )}
                          </td>
                          <td>{formatPrice(price)}</td> {/* FIXED: Use formatPrice */}
                          <td>{quantity}</td>
                          <td>{formatPrice(subtotal)}</td> {/* FIXED: Use formatPrice */}
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="4" className="total-label">Total Amount:</td>
                      <td className="total-value">{formatPrice(getOrderTotal(selectedOrder))}</td> {/* FIXED: Use formatPrice */}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <button className="close-btn" onClick={closeModal}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Image Zoom Popup ---------- */}
      {showImagePopup && selectedImage && (
        <div className="image-popup-overlay" onClick={closeImagePopup}>
          <div className="image-popup-content" onClick={(e) => e.stopPropagation()}>
            <button className="image-popup-close" onClick={closeImagePopup}>×</button>
            <div className="image-popup-header">
              <h4>{selectedImage.name}</h4>
            </div>
            <div className="image-popup-body">
              <img 
                src={selectedImage.url} 
                alt={selectedImage.name} 
                className="image-popup-img"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;